import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from '@/api/business';
import { deleteNonResearchReason } from '@/api/business/nonResearchReason';
import { NonResearchReason } from '@/types/NonResearchReason';
import Button from '@/components/Button';

interface DeleteDialogProps {
  isOpen: boolean;
  onClose: () => void;
  reason: NonResearchReason | null;
}

const DeleteDialog = ({ isOpen, onClose, reason }: DeleteDialogProps) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { mutate, isPending } = useMutation({
    mutationFn: deleteNonResearchReason,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['non-research-reason'] });
      toast({
        title: 'Sucesso',
        description: 'Motivo de não pesquisa removido com sucesso!',
      });
      onClose();
    },
    onError: (err) => {
      if (isAxiosError(err)) {
        toast({
          title: 'Erro ao remover',
          description:
            err.response?.data?.message ||
            'Não foi possível remover o motivo.',
          variant: 'destructive',
        });
      }
    },
  });

  const handleDelete = () => {
    if (!reason) return;
    mutate({ SK_MOTIVO_NAO_PESQUISA: reason.SK_MOTIVO_NAO_PESQUISA });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Excluir Motivo</DialogTitle>
          <DialogDescription>
            Tem certeza que deseja excluir o motivo{' '}
            <strong className="text-foreground">
              "{reason?.DS_MOTIVO_NAO_PESQUISA}"
            </strong>
            ? Esta ação removerá a opção para novas pesquisas mantendo o histórico de pesquisas já realizadas.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" type="button" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="destructive" onClick={handleDelete} disabled={isPending}>
            {isPending ? 'Excluindo...' : 'Excluir'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteDialog;
